import { Component, inject, Inject, OnInit, signal, WritableSignal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { DataService } from '../../../service/data.service';
import { LayoutService } from '../../../service/layout.service';
import { Company } from '../../../interface/company';
import { MigrateCommonService } from 'src/app/service/migrate-common.service';
import { MkgOS } from 'src/app/class/mkg-os';
import { MigrateService, SEFAZ_CODES } from 'src/app/service/migrate.service';
import { BoletoService } from 'src/app/service/boleto.service';
import { Boleto } from 'src/app/interface/boleto-pjbank';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { environment } from 'src/environments/environment';
import { CashTitle } from 'src/app/interface/cash-title';
import { LocalStorageService } from '@app/service/local.storage.service';
import { AllLaborsInvoiceModels, AllPartsInvoiceModels } from '@app/interface/mkgo-invoice';
import { IssueOsInvoiceResponse } from '@app/component/page/company/os/services/invoice-os.service';
import { NfUtilities } from '@app/class/nf-utilities';

type TitleOrigin = 'matrix' | 'filial';

export type BoletoTitleGroup = {
  cnpj: string;
  companyId: string;
  titleIds: string[];
  kind: 'parts' | 'labors' | 'unified';
};

type EmissionStatus = "generating" | "fiscal_rejection" | "success" | "error";

export interface AskRedirectComponentDataType {
  emission: {
    parts?: {
      model: AllPartsInvoiceModels;
      company: Company;
      status: WritableSignal<EmissionStatus>,
      statusMessage: WritableSignal<string>,
      response: IssueOsInvoiceResponse['partInvoice']
    },
    labors?: {
      model: AllLaborsInvoiceModels;
      company: Company;
      status: WritableSignal<EmissionStatus>,
      statusMessage: WritableSignal<string>,
      response: IssueOsInvoiceResponse['laborInvoice']
    }
  }
  titles: {
    all: CashTitle[];
    matrix: CashTitle[];
    filial: CashTitle[];
    boleto?: string[];
    boletoTitleGroups?: BoletoTitleGroup[];
  }
  osAsk: MkgOS;
  matrixCompanyId?: string;
  filialCompanyId?: string;
}

export type AskRedirectDialogResult = {
  redirectedTo: "invoice" | "budget" | "invoice_list" | "titles" | "boleto";
  createdBoleto?: Boleto
} | undefined;

@Component({
  selector: 'app-ask-redirect',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatDialogTitle,
    MatDialogContent,
    MatButtonModule,
    MatIcon,
    MatDialogClose,
    MatProgressSpinner,
    MatDialogActions,
  ],
  templateUrl: './ask-redirect.component.html',
  styleUrls: ['./ask-redirect.component.scss']
})
export class AskRedirectComponent implements OnInit {
  private readonly _dialogWidth = 'clamp(340px, 72vw, 920px)';
  private readonly _pdfWindowFeatures = 'popup=yes,width=900,height=700,left=120,top=80,resizable=yes,scrollbars=yes';

  createdBoleto = signal<Boleto | null>(null);
  creatingBoleto = signal(false);
  showMatrixTitlesButton = false;
  showFilialTitlesButton = false;
  private matrixOrigin: TitleOrigin = 'matrix';
  private filialOrigin: TitleOrigin = 'filial';

  readonly isFiscalParts: boolean = false;
  readonly isFiscalLabors: boolean = false;
  readonly hasAnyTitles: boolean = false;

  private _migrateCommonService = inject(MigrateCommonService);

  constructor(
    @Inject(MAT_DIALOG_DATA) protected readonly data: AskRedirectComponentDataType,
    public dataService: DataService,
    public layout: LayoutService,
    private boletoService: BoletoService,
    private _migrateService: MigrateService,
    private router: Router,
    private _localStorageService: LocalStorageService,
    private dialogRef: MatDialogRef<AskRedirectComponent, AskRedirectDialogResult>,
  ) {
    this.isFiscalParts = NfUtilities.isFiscal(this.data.emission.parts?.model);
    this.isFiscalLabors = NfUtilities.isFiscal(this.data.emission.labors?.model);
    this.hasAnyTitles = this._hasTitles(this.data.titles.matrix) || this._hasTitles(this.data.titles.filial);
  }

  ngOnInit() {
    this.dialogRef.updateSize(this._dialogWidth);
    this.configureTitlesButtonsView();
    this._readResponses()
  }

  canCreateBoleto() {
    return this.hasBoletoTitles()
      && !this.createdBoleto()
      && !!this.data.osAsk?.client
      && this.data.osAsk.client.document !== environment.DEFAULT_CLIENT_DOCUMENT;
  }

  private async _readResponses() {
    const emissionParts = this.data.emission.parts;
    const emissionLabors = this.data.emission.labors;
    if (emissionParts) {
      const nf = await this._migrateService.getNFById(emissionParts.response.id, emissionParts.company.cnpj);
      if (!nf) {
        emissionParts.status.set("error");
        emissionParts.statusMessage.set(`${emissionParts.model} de id ${emissionParts.response.id} não encontrada.`);
      } else {
        const rejection = MigrateCommonService.getRejectionsNF(nf);
        if (!rejection || (rejection?.migrateCode && rejection?.sefazCode && rejection?.migrateCode === 100 && rejection?.sefazCode === SEFAZ_CODES.AUTORIZADO)) {
          emissionParts.status.set("success");
          emissionParts.statusMessage.set("");
        } else {
          emissionParts.status.set("fiscal_rejection");
          const rejectionCode = rejection.sefazCode ?? rejection.migrateCode;
          const rejectionDescription = rejection.sefazDescription ?? rejection.migrateDescription;
          emissionParts.statusMessage.set(`${rejectionCode}: ${rejectionDescription}`);
        }
      }
    }

    if (emissionLabors) {
      const nfse = await this._migrateService.getNFSEById(emissionLabors.response.id, emissionLabors.company.cnpj);
      if (!nfse) {
        emissionLabors.status.set("error");
        emissionLabors.statusMessage.set(`${emissionLabors.model} de id ${emissionLabors.response.id} não encontrada.`);
      } else {
        const rejection = MigrateCommonService.getRejectionsNFS(nfse);
        if (!rejection || (rejection.migrateCode && rejection.sefazCode && rejection.migrateCode === 100 && rejection.sefazCode === SEFAZ_CODES.AUTORIZADO)) {
          emissionLabors.status.set("success");
          emissionLabors.statusMessage.set("");
        } else {
          const rejectionCode = rejection.sefazCode ?? rejection.migrateCode;
          const rejectionDescription = rejection.sefazDescription ?? rejection.migrateDescription;
          emissionLabors.status.set("fiscal_rejection");
          emissionLabors.statusMessage.set(`${rejectionCode}: ${rejectionDescription}`);
        }
      }
    }
  }

  async createBoleto(): Promise<void> {
    if (this.creatingBoleto()) {
      return;
    }

    this.creatingBoleto.set(true);
    const titles = this.data.titles.boleto;
    const os = this.data.osAsk;
    const boletoTitleGroups = this.data.titles.boletoTitleGroups?.filter((group) => !!group.titleIds?.length) || [];

    try {
      if (boletoTitleGroups.length) {
        const createdBoletos: Boleto[] = [];

        for (const group of boletoTitleGroups) {
          const boletos = await this.boletoService.createBoletoFromOs({ osId: os.id, titles: group.titleIds, cnpj: group.cnpj });
          createdBoletos.push(...boletos);
        }

        this.createdBoleto.set(createdBoletos[0] ?? null);
        return;
      }

      const boletos = await this.boletoService.createBoletoFromOs({ osId: os.id, titles: titles });
      this.createdBoleto.set(boletos[0]);
    } catch (error) {
      console.error('Error creating boleto:', error);
      this.createdBoleto.set(null);
    } finally {
      this.creatingBoleto.set(false);
    }
  }

  protected seeBoleto(): void {
    const boleto = this.createdBoleto();
    const link = boleto?.group_link || boleto?.boleto_link;

    if (!link) {
      this.router.navigate(['/company', this.dataService.company?.id, 'financial', 'boletos']);
      this.dialogRef.close({ redirectedTo: 'boleto', createdBoleto: boleto });
      return;
    }

    window.open(link, '_blank', 'noopener,noreferrer');
    this.dialogRef.close({ redirectedTo: 'boleto', createdBoleto: boleto });
  }

  private hasBoletoTitles(): boolean {
    return !!this.data.titles.boleto?.length || !!this.data.titles.boletoTitleGroups?.some((group) => !!group.titleIds?.length);
  }

  private configureTitlesButtonsView(): void {
    if (!this.hasAnyTitles) {
      this.showMatrixTitlesButton = false;
      this.showFilialTitlesButton = false;
      return;
    }

    const hasMatrixTitles = this._hasTitles(this.data.titles.matrix);
    const hasFilialTitles = this._hasTitles(this.data.titles.filial);

    const matrixCompanyId = this._getTitlesCompanyId('matrix');
    const filialCompanyId = this._getTitlesCompanyId('filial');
    const currentCompanyId = this.dataService.company?.id;

    if (matrixCompanyId && filialCompanyId && matrixCompanyId === filialCompanyId) {
      this.matrixOrigin = hasMatrixTitles ? 'matrix' : 'filial';
      this.filialOrigin = this.matrixOrigin === 'matrix' ? 'filial' : 'matrix';
      this.showMatrixTitlesButton = hasMatrixTitles || hasFilialTitles;
      this.showFilialTitlesButton = false;
      return;
    }

    if (currentCompanyId && filialCompanyId && currentCompanyId === filialCompanyId) {
      this.matrixOrigin = 'filial';
      this.filialOrigin = 'matrix';
      this.showMatrixTitlesButton = hasFilialTitles;
      this.showFilialTitlesButton = hasMatrixTitles;
      return;
    }

    this.matrixOrigin = 'matrix';
    this.filialOrigin = 'filial';
    this.showMatrixTitlesButton = hasMatrixTitles;
    this.showFilialTitlesButton = hasFilialTitles;
  }

  private _getTitlesCompanyId(origin: TitleOrigin): string | undefined {
    if (origin === 'matrix') {
      return this.data.matrixCompanyId || this.data.titles.matrix?.find((title) => !!title.company)?.company;
    }

    return this.data.filialCompanyId || this.data.titles.filial?.find((title) => !!title.company)?.company;
  }

  private _hasTitles(titles: CashTitle[] | undefined): boolean {
    return Array.isArray(titles) && titles.length > 0;
  }

  protected seeInvoiceOf(type: "parts" | "labors") {
    const emissionConfig = this.data.emission[type];
    const companyId = emissionConfig?.company.id || this.dataService.company?.id;
    this.router.navigate(['/company', companyId, 'invoices', type == "parts" ? "nf" : "nfse", emissionConfig.response?.id]);
    this.dialogRef.close({ redirectedTo: "invoice" });
  }

  protected goToCreateNewBudget() {
    this.router.navigate(['/company', this.dataService.company?.id, 'os', 'create']);
    this.dialogRef.close({ redirectedTo: "budget" });
  }

  protected goToInvoiceList() {
    this.router.navigate(['/company', this.dataService.company?.id, 'invoices']);
    this.dialogRef.close({ redirectedTo: "invoice_list" });
  }

  protected goToTitlesOf(origin: TitleOrigin) {
    const resolvedOrigin = origin === 'matrix' ? this.matrixOrigin : this.filialOrigin;
    const targetCompanyId = this._getTitlesCompanyId(resolvedOrigin) || this.dataService.company?.id;
    if (!targetCompanyId) return
    this._localStorageService.saveDataJSON('cash-title-cache', { filter: { companyCode: this.data.osAsk.codeSystem } });
    this.router.navigate(['/company', targetCompanyId, 'financial', 'cash-title']);
    this.dialogRef.close({ redirectedTo: "titles" });

  }

  protected seeInvoicePDF(type: "parts" | "labors") {
    const emissionConfig = type === "parts" ? this.data.emission.parts : this.data.emission.labors;
    if (!emissionConfig?.response?.id || !emissionConfig.model) {
      return;
    }

    const invoiceId = emissionConfig.response.id;

    void (async () => {
      try {
        const { blob } = await this._migrateCommonService.downloadInvoiceFile({
          invoiceId,
          model: emissionConfig.model,
          fileType: "pdf",
          companyCnpj: emissionConfig.company.cnpj
        });
        const url = window.URL.createObjectURL(blob);

        const targetWindow = window.open('', 'mkgo-os-invoice-pdf', this._pdfWindowFeatures);
        if (!targetWindow) {
          window.URL.revokeObjectURL(url);
          return;
        }

        targetWindow.location.href = url;
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      } catch (error) {
        console.error('PDF download failed:', error);
      }
    })();
  }

  protected downloadInvoiceXML(type: "parts" | "labors") {
    const emissionConfig = type === "parts" ? this.data.emission.parts : this.data.emission.labors;
    if (!emissionConfig?.response?.id || !emissionConfig.model) {
      return;
    }

    const invoiceId = emissionConfig.response.id;

    void (async () => {
      try {
        const { blob, filename } = await this._migrateCommonService.downloadInvoiceFile({
          invoiceId,
          model: emissionConfig.model,
          fileType: "xml",
          companyCnpj: emissionConfig.company.cnpj
        });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('XML download failed:', error);
      }
    })();
  }

}
